import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Divider,
  Chip,
  Tooltip
} from '@mui/material'
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where
} from 'firebase/firestore'
import { getMetadata, listAll, ref } from 'firebase/storage'
import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

const SuperAdmin = ({ uid, db, storage }) => {
  const [documents, setDocuments] = useState({})
  const [next, setNext] = useState(true)
  const navigate = useNavigate()
  const [lastVisibleUser, setLastVisibleUser] = useState(null)
  const limitLength = 5

  const getQuery = (_orderBy, lastVisible) => {
    return lastVisible
      ? query(
          collection(db, 'users'),
          where('paid', '==', true),
          orderBy(_orderBy, 'desc'),
          startAfter(lastVisible),
          limit(limitLength)
        )
      : query(
          collection(db, 'users'),
          where('paid', '==', true),
          orderBy(_orderBy, 'desc'),
          limit(limitLength)
        )
  }

  async function getPaidUsers () {
    let first = getQuery('documentCount', null)
    const documentSnapshots = await getDocs(first)
    if (documentSnapshots.empty) {
      setNext(false)
      return
    }
    const lastVisible =
      documentSnapshots.docs[documentSnapshots.docs.length - 1]
    setNext(documentSnapshots.docs.length === limitLength)
    setLastVisibleUser(lastVisible)

    const sortable = await getDocumentsFromIds(documentSnapshots, {})
    setDocuments(sortable)
  }

  async function getNewPaidUsers () {
    const documentSnapshots = await getDocs(
      getQuery('documentCount', lastVisibleUser)
    )
    if (documentSnapshots.empty) {
      setNext(false)
      return
    }
    const lastVisible =
      documentSnapshots.docs[documentSnapshots.docs.length - 1]
    setNext(documentSnapshots.docs.length === limitLength)
    setLastVisibleUser(lastVisible)

    const sortable = await getDocumentsFromIds(documentSnapshots, documents)
    setDocuments(sortable)
  }

  async function getDocumentsFromIds (userSnap, currentDocs) {
    let userDocs = { ...currentDocs }
    let ids = []
    userSnap.forEach(doc => {
      ids.push(doc.id)
      userDocs[doc.id] = {
        name: doc.data().displayName,
        email: doc.data().email,
        docs: []
      }
    })

    if (ids.length !== 0) {
      const collections = ['documents', 'sliders']
      for (const col of collections) {
        let q = query(collection(db, col), where('uid', 'in', ids))
        const snap = await getDocs(q)
        snap.forEach(doc => {
          let val = doc.data()
          val.id = doc.id
          if (!userDocs[val.uid].docs.some(d => d.id === val.id)) {
            userDocs[val.uid].docs.push(val)
          }
        })
      }
    }

    return Object.entries(userDocs)
      .sort(([, a], [, b]) => b.docs.length - a.docs.length)
      .reduce((r, [k, v]) => ({ ...r, [k]: v }), {})
  }

  useEffect(() => {
    if (db && storage) {
      if (uid !== 'ofOJBZqp7WdrpYYYPV16KAbotsd2') {
        navigate('/search')
      } else {
        getPaidUsers()
      }
    }
  }, [uid, db, storage])

  return (
    <Box sx={{ m: { md: 2, xs: 0 }, minHeight: '95%', pb: 4 }}>
      <div style={{ width: '95vw', margin: '10px auto' }}>
        <Typography variant='h4' gutterBottom>
          Super Admin - v1.3.0
        </Typography>
        <Stack spacing={2}>
          {Object.keys(documents).map(key => (
            <PersonInfoView
              key={key}
              id={key}
              storage={storage}
              document={documents[key]}
            />
          ))}
        </Stack>
        {next && (
          <Button variant='contained' sx={{ mt: 4 }} onClick={getNewPaidUsers}>
            Load More Users
          </Button>
        )}
      </div>
    </Box>
  )
}

const PersonInfoView = ({ id, storage, document }) => {
  const [fileInfo, setFileInfo] = useState({
    totalSize: '0 Bytes',
    logoSize: '0 Bytes',
    fileSize: '0 Bytes',
    biggestFile: '0 Bytes'
  })
  const [sortConfig, setSortConfig] = useState({
    key: 'period',
    direction: 'desc'
  })
  const navigate = useNavigate()

  useEffect(() => {
    const fetchStorageData = async () => {
      const listRef = ref(storage, id)
      let total = 0,
        logos = 0,
        filesOnly = 0,
        max = 0
      try {
        const res = await listAll(listRef)
        await Promise.all(
          res.items.map(async item => {
            const meta = await getMetadata(item)
            total += meta.size
            if (item.fullPath.includes('_logo_')) {
              logos += meta.size
            } else {
              filesOnly += meta.size
              if (meta.size > max) max = meta.size
            }
          })
        )
        setFileInfo({
          totalSize: formatSize(total),
          logoSize: formatSize(logos),
          fileSize: formatSize(filesOnly),
          biggestFile: formatSize(max),
          storageLink: `https://console.firebase.google.com/u/1/project/community-doc/storage/community-doc.appspot.com/files/~2F${id}`
        })
      } catch (e) {
        console.error(e)
      }
    }
    fetchStorageData()
  }, [id, storage])

  function formatSize (b) {
    const u = ['Bytes', 'KB', 'MB', 'GB']
    let s = b,
      i = 0
    while (s >= 1024 && i < u.length - 1) {
      s /= 1024
      i++
    }
    return `${s.toFixed(2)} ${u[i]}`
  }

  const handleRequestSort = property => {
    const isAsc = sortConfig.key === property && sortConfig.direction === 'asc'
    setSortConfig({ key: property, direction: isAsc ? 'desc' : 'asc' })
  }

  const sortedDocs = useMemo(() => {
    return [...document.docs].sort((a, b) => {
      let aV, bV
      if (sortConfig.key === 'type') {
        aV = a.pulseValues ? 1 : 0
        bV = b.pulseValues ? 1 : 0
      } else if (sortConfig.key === 'visitedBy') {
        aV = a.visitedBy?.length || 0
        bV = b.visitedBy?.length || 0
      } else if (sortConfig.key === 'period') {
        aV = a.visibleDate?.startDate
          ? a.visibleDate.startDate.toDate().getTime()
          : 0
        bV = b.visibleDate?.startDate
          ? b.visibleDate.startDate.toDate().getTime()
          : 0
      } else {
        aV =
          a[sortConfig.key === 'comments' ? 'commentCount' : sortConfig.key] ||
          0
        bV =
          b[sortConfig.key === 'comments' ? 'commentCount' : sortConfig.key] ||
          0
      }
      return sortConfig.direction === 'asc' ? aV - bV : bV - aV
    })
  }, [document.docs, sortConfig])

  const getDate = date => {
    return new Date(date.toDate())
  }

  function getDateString (date) {
    const options = { year: 'numeric', month: 'numeric', day: 'numeric' }
    return new Date(date.toDate()).toLocaleDateString(undefined, options)
  }

  function getComboDateString (obj) {
    const start =
      obj && obj.startDate ? new Date(getDateString(obj.startDate)) : null
    const end = obj && obj.endDate ? new Date(getDateString(obj.endDate)) : null

    const fmt = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })

    return `${start ? fmt.format(start) : 'undefined'} – ${
      end ? fmt.format(end) : 'undefined'
    }`
  }

  const getDates = document => {
    if (document.visibleDate) {
      return getComboDateString(document.visibleDate)
    } else {
      return 'No Visible Date'
    }
  }

  const checkDates = document => {
    const today = Date.now()
    return (
      document.visibleDate &&
      today >= getDate(document.visibleDate.startDate) &&
      today <= getDate(document.visibleDate.endDate)
    )
  }

  const olderThanYear = document => {
    const sixMonthsAgo = Date.now() - 365 * 24 * 60 * 60 * 1000
    return (
      document.visibleDate &&
      getDate(document.visibleDate.endDate).getTime() < sixMonthsAgo
    )
  }

  return (
    <Card raised>
      <CardHeader
        title={`${document.name} (${document.email})`}
        subheader={`UID: ${id}`}
      />
      <Divider />
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={4}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant='subtitle2' color='textSecondary'>
              STORAGE METRICS
            </Typography>
            <Typography variant='body2'>Total: {fileInfo.totalSize}</Typography>
            <Typography variant='body2'>Files: {fileInfo.fileSize}</Typography>
            <Typography variant='body2'>Logos: {fileInfo.logoSize}</Typography>
            <Typography variant='body2'>
              Largest: {fileInfo.biggestFile}
            </Typography>
          </Box>
          <Box>
            <Typography variant='subtitle2' color='textSecondary'>
              ENGAGEMENT TOTALS
            </Typography>
            <Typography variant='body2'>
              Documents: {document.docs.length}
            </Typography>
            <Typography variant='body2'>
              Visits: {document.docs.reduce((a, v) => a + (v.visited || 0), 0)}
            </Typography>
            <Typography variant='body2'>
              Unique Visits:{' '}
              {document.docs.reduce(
                (a, v) => a + (v.visitedBy?.length || 0),
                0
              )}
            </Typography>
            <Typography variant='body2'>
              Comments:{' '}
              {document.docs.reduce((a, v) => a + (v.commentCount || 0), 0)}
            </Typography>
          </Box>

          <Button
            variant='outlined'
            href={fileInfo.storageLink}
            target='_blank'
          >
            Storage
          </Button>
        </Stack>

        {sortedDocs.length > 0 && (
          <TableContainer component={Paper} variant='outlined'>
            <Table size='small'>
              <TableHead sx={{ bgcolor: '#fafafa' }}>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'type'}
                      direction={sortConfig.direction}
                      onClick={() => handleRequestSort('type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align='right'>
                    <TableSortLabel
                      active={sortConfig.key === 'visitedBy'}
                      direction={sortConfig.direction}
                      onClick={() => handleRequestSort('visitedBy')}
                    >
                      Unique
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align='right'>
                    <TableSortLabel
                      active={sortConfig.key === 'comments'}
                      direction={sortConfig.direction}
                      onClick={() => handleRequestSort('comments')}
                    >
                      Comments
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'period'}
                      direction={sortConfig.direction}
                      onClick={() => handleRequestSort('period')}
                    >
                      Period
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align='center'>Link</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedDocs.map(doc => (
                  <TableRow key={doc.id} hover>
                    <TableCell>
                      <Tooltip
                        title={
                          checkDates(doc)
                            ? 'Live'
                            : olderThanYear(doc)
                            ? 'Older than a year'
                            : doc.pulseValues
                            ? 'Pulse'
                            : 'Document'
                        }
                      >
                        <Chip
                          label={doc.pulseValues ? 'PULSE' : 'DOCUMENT'}
                          size='small'
                          color={
                            checkDates(doc)
                              ? 'success'
                              : olderThanYear(doc)
                              ? 'error'
                              : doc.pulseValues
                              ? 'secondary'
                              : 'primary'
                          }
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>{doc.fileName}</TableCell>
                    <TableCell align='right'>
                      {doc.visitedBy?.length || 0} ({doc.visited || 0})
                    </TableCell>
                    <TableCell align='right'>{doc.commentCount || 0}</TableCell>
                    <TableCell>{getDates(doc)}</TableCell>
                    <TableCell align='center'>
                      <Button
                        size='small'
                        onClick={() =>
                          navigate(
                            (doc.pulseValues ? '/viewPulse/' : '/view/') +
                              doc.id
                          )
                        }
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )
}

export default SuperAdmin
